/**
 * Shared utility functions
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { slugifySkillName } from '@wayai/core';
import * as readline from 'node:readline';
import { expected } from './expected.js';
import { REPORT_EDIT_FLAGS } from './report-edit-args.js';
import { sanitizeTerminalText } from './terminal-output.js';

export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Prompt the user for free-text input.
 */
export function prompt(question: string): Promise<string> {
  const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
  return new Promise((resolve) => {
    rl.question(question, (answer) => {
      rl.close();
      resolve(answer.trim());
    });
  });
}

/**
 * Prompt the user for yes/no confirmation. Returns true if they answer 'y'.
 */
export function confirm(question: string): Promise<boolean> {
  const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
  return new Promise((resolve) => {
    rl.question(`${question} [y/N]: `, (answer) => {
      rl.close();
      resolve(answer.trim().toLowerCase() === 'y');
    });
  });
}

/** Prompt for a secret, masking typed characters with `*`. */
export function promptSecret(question: string): Promise<string> {
  return new Promise((resolve) => {
    const rl = readline.createInterface({ input: process.stdin, output: process.stdout });

    // Mute output by replacing _writeToOutput
    const originalWrite = (rl as unknown as { _writeToOutput: (s: string) => void })._writeToOutput;
    let firstWrite = true;
    (rl as unknown as { _writeToOutput: (s: string) => void })._writeToOutput = function (s: string) {
      if (firstWrite) {
        // Let the prompt question through
        originalWrite.call(rl, s);
        firstWrite = false;
      } else {
        // Replace typed characters with *
        originalWrite.call(rl, '*'.repeat(s.replace(/[\r\n]/g, '').length));
      }
    };

    rl.question(question, (answer) => {
      // Restore and print newline
      (rl as unknown as { _writeToOutput: (s: string) => void })._writeToOutput = originalWrite;
      process.stdout.write('\n');
      rl.close();
      resolve(answer);
    });
  });
}

/** Read all of stdin as a single trimmed string (for piped input). */
export function readStdin(): Promise<string> {
  return new Promise((resolve, reject) => {
    let data = '';
    process.stdin.setEncoding('utf-8');
    process.stdin.on('data', (chunk) => { data += chunk; });
    process.stdin.on('end', () => resolve(data.trim()));
    process.stdin.on('error', reject);
  });
}

/**
 * Kebab/snake slug for hub folders + skill names — the canonical
 * `slugifySkillName` from @wayai/core. The CLI now bundles @wayai/core (tsup
 * `noExternal`), so this is a real runtime import, not a hand-kept copy.
 */
export const slugify = slugifySkillName;

/**
 * Resolve the hub YAML path in a folder: tries hub.yaml first, falls back to wayai.yaml.
 * Returns the full path if found, null otherwise.
 */
export function resolveHubYamlPath(hubFolder: string): string | null {
  for (const filename of ['hub.yaml', 'wayai.yaml']) {
    const yamlPath = path.join(hubFolder, filename);
    if (fs.existsSync(yamlPath)) return yamlPath;
  }
  return null;
}

/**
 * Parse a shorthand period string (e.g. "7d", "4w", "3m", "1y") into
 * { start, end, periodicity } for analytics queries.
 */
export function parsePeriod(period: string): { start: string; end: string; periodicity: string } {
  const match = period.match(/^(\d+)([hdwmy])$/);
  if (!match) {
    throw new Error(`Invalid period "${period}". Use format like 24h, 7d, 4w, 3m, 1y.`);
  }

  const amount = parseInt(match[1], 10);
  const unit = match[2];

  if (amount <= 0) {
    throw new Error(`Invalid period "${period}". Amount must be greater than 0.`);
  }

  const now = new Date();
  const start = new Date(now);

  switch (unit) {
    case 'h':
      start.setHours(start.getHours() - amount);
      break;
    case 'd':
      start.setDate(start.getDate() - amount);
      break;
    case 'w':
      start.setDate(start.getDate() - amount * 7);
      break;
    case 'm':
      start.setMonth(start.getMonth() - amount);
      break;
    case 'y':
      start.setFullYear(start.getFullYear() - amount);
      break;
  }

  const periodicityMap: Record<string, string> = {
    h: 'daily',
    d: 'daily',
    w: 'weekly',
    m: 'monthly',
    y: 'yearly',
  };

  return {
    start: start.toISOString(),
    end: now.toISOString(),
    periodicity: periodicityMap[unit],
  };
}

/**
 * Parse --from/--to date strings into ISO format.
 * Accepts YYYY-MM-DD or full ISO datetime. Appends time suffix if needed.
 */
export function parseDateArg(date: string, position: 'from' | 'to'): string {
  if (date.includes('T')) {
    if (!/^\d{4}-\d{2}-\d{2}T/.test(date) || isNaN(new Date(date).getTime())) {
      throw new Error(`Invalid date format "${date}". Use YYYY-MM-DD or full ISO datetime.`);
    }
    // Verify the date portion doesn't overflow (e.g., Feb 31 → Mar 3)
    const datePart = date.slice(0, 10);
    const [year, month, day] = datePart.split('-').map(Number);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
      throw new Error(`Invalid date "${date}". Not a valid calendar date.`);
    }
    return date;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    throw new Error(`Invalid date format "${date}". Use YYYY-MM-DD (e.g., 2026-01-01).`);
  }
  // Verify the date components are valid (JS Date silently overflows, e.g., Feb 31 → Mar 3)
  const [year, month, day] = date.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
    throw new Error(`Invalid date "${date}". Not a valid calendar date.`);
  }
  return position === 'from' ? `${date}T00:00:00Z` : `${date}T23:59:59Z`;
}

/**
 * Resolve organization_id from: --org flag → the repo's org binding → single-org auto-select.
 * Returns { organizationId, organizationName } or exits with an error.
 */
export async function resolveOrganizationId(
  client: { organizations(): Promise<{ organizations: Array<{ id: string; name: string }> }> },
  orgIdFlag?: string,
): Promise<{ organizationId: string; organizationName?: string }> {
  if (orgIdFlag) {
    if (!UUID_RE.test(orgIdFlag)) {
      console.error('Invalid --org value. Expected a UUID.');
      process.exit(1);
    }
    return { organizationId: orgIdFlag };
  }

  // Try the repo's org binding (wayai-ws/wayai.yaml, or the deprecated root file)
  // Lazy import to avoid circular dependency
  // NOT `readRepoConfig`: absent falls through to the single-org auto-select
  // below, so a repo whose two config files disagree would silently create the
  // credential in whichever organization happens to be the only one.
  const { readRepoConfigUnlessBlocked } = await import('./repo-config.js');
  const repoConfig = readRepoConfigUnlessBlocked();
  if (repoConfig) {
    return { organizationId: repoConfig.organization_id, organizationName: repoConfig.organization_name };
  }

  // Fetch orgs and auto-select if single
  const { organizations } = await client.organizations();
  if (organizations.length === 0) {
    console.error('No organizations found. Create one in the WayAI platform first.');
    process.exit(1);
  }
  if (organizations.length === 1) {
    console.log(`Organization: ${organizations[0].name}`);
    return { organizationId: organizations[0].id, organizationName: organizations[0].name };
  }

  console.error('Multiple organizations found. Specify one with --org <uuid>:');
  for (const org of organizations) {
    console.error(`  ${org.id}  ${org.name}`);
  }
  process.exit(1);
}

export function parsePositiveIntFlag(name: string, raw: string | undefined): number {
  if (raw === undefined) {
    console.error(`${name} requires a value`);
    process.exit(1);
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
    console.error(`${name} must be a positive integer.`);
    process.exit(1);
  }
  return n;
}

/**
 * Subcommands that render their own richer `--help`/`-h` output (and never run a
 * side effect on it). These fall through to their handler so the global-help
 * interception in `main()` doesn't shadow their command-specific help:
 *   eval          → commands/eval.ts (explicit case '--help')
 *   admin         → commands/admin.ts (fall-through printHelp)
 *   report        → commands/report.ts (fall-through printReportHelp)
 *   conversations → commands/conversations.ts (early wantsHelp → printConversationsHelp);
 *                   without this, `conversations --help` hides the `observability` subcommand
 *   communities   → commands/communities.ts (early wantsHelp → its usage)
 *   contacts      → commands/contacts.ts (early wantsHelp → its usage)
 *   knowledge     → commands/knowledge.ts (early wantsHelp → its usage)
 *
 * Single source of truth — `index.ts` consumes this for dispatch and the tests
 * assert against it directly, so a member can't silently drift out of the real
 * set without a test catching it.
 */
export const OWN_HELP_COMMANDS: ReadonlySet<string> = new Set([
  'eval',
  'admin',
  'report',
  'conversations',
  'alerts',
  'communities',
  'contacts',
  'knowledge',
]);

/**
 * Low-level predicate: true when `--help`/`-h` appears anywhere in args.
 *
 * The match is deliberately broad (and so fail-safe — printing help is
 * non-destructive). The one realistic command that takes a free-text value that
 * could *equal* `--help` is `report` (`report create --description "--help"`),
 * and that command owns its help handling so it's excluded from interception by
 * `shouldInterceptHelp` — its own parser sees the value verbatim. Used both to
 * gate that interception and to skip the update check on any help invocation.
 */
export function wantsHelp(args: string[]): boolean {
  return args.includes('--help') || args.includes('-h');
}

/**
 * Whether the top-level entry should short-circuit to the GLOBAL help BEFORE
 * dispatching `command`. This stops side-effecting subcommands (e.g.
 * `run-eval`, which would otherwise create + run a billable eval session) from
 * executing on a `--help`/`-h` request.
 *
 * Returns false — i.e. falls through to the command's own handler — for:
 * - the top-level `help` slot (handled by the dispatch switch itself),
 * - an absent command, and
 * - commands in `ownHelpCommands` (e.g. `eval`, `admin`, `report`), which
 *   render their own richer command-specific help; intercepting them here would
 *   downgrade that to the global help.
 */
export function shouldInterceptHelp(
  command: string | undefined,
  args: string[],
  ownHelpCommands: ReadonlySet<string>,
): boolean {
  if (!command || command === 'help') return false;
  if (ownHelpCommands.has(command)) return false;
  return wantsHelp(args);
}

/**
 * What a hand parser reads, for `entryFlagRefusal`. Every flag outside `values` it reads bare, if at
 * all: the hand parsers take a value only after the flags they know.
 */
export interface ParserReads {
  /** How many positional arguments it reads after the command's own words (`Infinity`: all). */
  positionals: number;
  /** The flags it reads a value after: `--hub <value>`. */
  values?: readonly string[];
  /** Those of `values` it also reads spelled `--flag=<value>`. */
  equals?: readonly string[];
  /** It reads `""` as a value of each of `values` (`parseReportEditArgs`, where `""` clears a field). */
  readsEmpty?: true;
  /** It reads a bare `--dry-run`. */
  dryRun?: true;
}

/** A `PARSER_READS` entry: what a hand parser reads, or one of the two parsers not read here. */
type ParserEntry = 'all' | 'selectors' | ParserReads;

/** The `wayai` words that run no command's parser: the help and version slots. */
export const HELP_AND_VERSION: readonly string[] = ['help', '--help', '-h', '--version', '-v'];


/**
 * The parser behind every command `wayai` dispatches, keyed by the words that reach it
 * (`registry.test.ts` fails when a dispatched command has no entry, or an entry no command). An
 * entry changes with its parser, in the same change: nothing checks one against the other, and a
 * value flag or positional missing here is refused before the parser sees it. Two kinds are not
 * read here:
 *  - `all`: the parser reads or refuses everything `entryFlagRefusal` checks. `pull`/`push` (the
 *    subtree router, per side), and `communities`, `contacts` and `knowledge` (`util.parseArgs` in
 *    strict mode).
 *  - `selectors`: `resolveScopeSelector` refuses an empty selector and a valued `--add` itself; only
 *    the `--dry-run` check applies.
 */
export const PARSER_READS: ReadonlyMap<string, ParserEntry> = new Map<string, ParserEntry>([
  ['pull', 'all'],
  ['push', 'all'],
  ['communities', 'all'],
  ['contacts', 'all'],
  ['knowledge', 'all'],
  ['use', 'selectors'],
  ['unbind', 'selectors'],
  ['unlock', 'selectors'],
  ['login', { positionals: 0, values: ['--api-url'] }],
  ['logout', { positionals: 0 }],
  ['status', { positionals: 0 }],
  ['whoami', { positionals: 0 }],
  ['init', { positionals: 0, values: ['--org'] }],
  ['create', { positionals: 1, values: ['--label'] }],
  ['diff', { positionals: 0, values: ['--hub'] }],
  ['replicate', { positionals: 1, values: ['--label', '--hub'] }],
  ['relabel', { positionals: 1, values: ['--hub'] }],
  ['publish', { positionals: 0, values: ['--hub'] }],
  ['sync', { positionals: 0, values: ['--hub'] }],
  ['migrate', { positionals: 0 }],
  ['send-message', { positionals: 1, values: ['--hub', '-c', '--conversation', '-f', '--file'] }],
  [
    'conversations',
    {
      positionals: 2,
      values: ['--hub', '--org', '--status', '--limit', '--offset', '--message-id', '--period', '--from', '--to', '--set', '--type'],
      equals: ['--org'],
    },
  ],
  ['alerts', { positionals: 0, values: ['--hub'] }],
  [
    'delete-history',
    { positionals: 0, values: ['--hub', '--user-id', '--conversation-id'], equals: ['--user-id', '--conversation-id'] },
  ],
  ['sync-skills', { positionals: 0, values: ['--hub', '--connection-id'] }],
  ['sync-mcp', { positionals: 0, values: ['--hub', '--connection'], dryRun: true }],
  ['evals', { positionals: 0, values: ['--hub'] }],
  ['evals sql', { positionals: 1, values: ['--hub', '--limit'] }],
  ['run-eval', { positionals: 0, values: ['--hub', '--timeout', '--set', '--eval', '--runs', '--pacing', '--fixture'] }],
  ['eval-results', { positionals: 0, values: ['--hub', '--session', '--eval'] }],
  [
    'eval call',
    {
      positionals: 0,
      values: [
        '--hub', '--journey', '--runs', '--plan', '--clips', '--tts-voice', '--tts-model', '--max-call-seconds',
        '--max-operations', '--usd-per-minute', '--max-usd', '--latency-p95-ms', '--json',
      ],
    },
  ],
  ['eval capture', { positionals: 1, values: ['--hub', '--set', '--name', '--instructions'] }],
  ['eval journey capture', { positionals: 1, values: ['--hub', '--name', '--instructions'] }],
  ['eval session stop', { positionals: 1, values: ['--hub'] }],
  ['eval session delete', { positionals: 1, values: ['--hub'] }],
  ['analytics', { positionals: 0, values: ['--hub', '--period', '--from', '--to', '--metric', '--filter'] }],
  [
    'analytics query',
    { positionals: 0, values: ['--hub', '--metric', '--group-by', '--order-by', '--filter', '--from', '--to', '--limit'] },
  ],
  ['analytics sql', { positionals: 1, values: ['--hub', '--limit'] }],
  ['create-credential', { positionals: 0, values: ['--name', '--type', '--org', '--description', '--tag', '--environment'] }],
  ['update-credential', { positionals: 0, values: ['--name', '--rename', '--org', '--description', '--tag', '--environment'] }],
  ['set-connection-credential', { positionals: 0, values: ['--hub', '--connection', '--org-credential', '--field'] }],
  ['org create', { positionals: 1, values: ['--region'] }],
  ['org pull', { positionals: 0 }],
  ['org diff', { positionals: 0 }],
  ['org push', { positionals: 0 }],
  ['list', { positionals: 0, values: ['--org'] }],
  [
    'report create',
    {
      positionals: 0,
      values: [
        '-t', '--title', '-d', '--description', '--source', '--classification', '--dedup-key', '--hub', '--conversation',
        '-e', '--error', '-s', '--steps', '--context',
      ],
    },
  ],
  ['report edit', { positionals: 1, values: REPORT_EDIT_FLAGS, readsEmpty: true }],
  ['report list', { positionals: 0, values: ['--status'] }],
  ['report get', { positionals: 1 }],
  ['report accept', { positionals: 1 }],
  ['report contest', { positionals: 1, values: ['--reason'] }],
  ['update', { positionals: 0 }],
  ['admin archive', { positionals: 2, values: ['--message-id'] }],
  ['admin observability', { positionals: 2, values: ['--message-id'] }],
  ['admin do tables', { positionals: 2 }],
  ['admin do read', { positionals: 3, values: ['--limit', '--id'] }],
  ['admin analytics tables', { positionals: 0 }],
  ['admin analytics read', { positionals: 1, values: ['--limit', '--conversation-id', '--hub-id', '--message-id'] }],
  ['admin report list', { positionals: 0, values: ['--status', '--source', '--limit', '--offset', '--since', '--resolved-since'] }],
  ['admin report show', { positionals: 1 }],
  ['admin report group', { positionals: Infinity }],
  ['admin report ungroup', { positionals: 1 }],
  ['admin report escalate', { positionals: 1, values: ['--gh', '--verdict'] }],
  ['admin report shipped', { positionals: 1, values: ['--note'] }],
  ['admin report addressed', { positionals: 1 }],
  ['admin report dismiss', { positionals: 1, values: ['--reason', '--verdict'] }],
  ['admin report hold', { positionals: 1, values: ['--reason'] }],
  ['admin report update', { positionals: 1, values: REPORT_EDIT_FLAGS, readsEmpty: true }],
  ['admin people list', { positionals: 0, values: ['--since', '--limit', '--offset'] }],
  ['admin people show', { positionals: 1 }],
  ['admin people dismiss', { positionals: 1, values: ['--note'] }],
  ['admin people remove-message', { positionals: 1, values: ['--message', '--note'] }],
  ['admin people user', { positionals: 1 }],
  ['admin people suspend', { positionals: 1, values: ['--report', '--note'] }],
  ['admin people unsuspend', { positionals: 1, values: ['--note'] }],
  ['admin notice create', { positionals: 0, values: ['--title', '--severity', '--scope', '--body', '--link', '--starts', '--ends'] }],
  ['admin notice list', { positionals: 0, values: ['--status', '--limit', '--offset'] }],
  ['admin notice resolve', { positionals: 1 }],
  ['admin skill install', { positionals: 1 }],
  ['admin hubs repair-team-orphans', { positionals: 0, values: ['--org', '--hub'], dryRun: true }],
  ['admin hubs repair-branching-orphans', { positionals: 0, values: ['--org', '--hub', '--id'], dryRun: true }],
  ['admin orgs repair-legacy-grants', { positionals: 0, values: ['--org'], dryRun: true }],
  ['admin harness mass-destroy', { positionals: 0, values: ['--org', '--hub'], dryRun: true }],
  ['admin harness disable', { positionals: 0 }],
  ['admin harness enable', { positionals: 0 }],
  ['admin harness status', { positionals: 0 }],
  ['admin threads enable', { positionals: 0 }],
  ['admin threads disable', { positionals: 0 }],
  ['admin threads status', { positionals: 0 }],
  ['admin communities enable', { positionals: 0 }],
  ['admin communities disable', { positionals: 0 }],
  ['admin communities status', { positionals: 0 }],
  ['admin communities create', { positionals: 0, values: ['--org', '--name'] }],
  ['admin communities destroy', { positionals: 1 }],
  ['admin guide set-hub', { positionals: 1 }],
  ['admin guide clear', { positionals: 0 }],
  ['admin guide status', { positionals: 0 }],
  ['admin voice-calls enable', { positionals: 0 }],
  ['admin voice-calls disable', { positionals: 0 }],
  ['admin voice-calls status', { positionals: 0 }],
  ['admin voice-calls price', { positionals: 0, values: ['--ops-per-minute'] }],
]);

/**
 * The dispatchers that hand their verb every argument before the first `--` flag as a positional, and
 * every argument from it on as a flag argument: `admin` (`splitArgs` in `commands/admin.ts`).
 */
const SPLITS_AT_FIRST_LONG_FLAG: ReadonlySet<string> = new Set(['admin']);

/** The target a value flag's refusal names; any other flag needs `a value`. */
const FLAG_TARGETS: Readonly<Record<string, string>> = { '--hub': 'a hub', '--org': 'an organization' };

/** A flag and its value spelled as one argument, `--flag=<value>`, split at the first `=`. */
const EQUALS_SPELLING = /^(--?[A-Za-z][A-Za-z0-9-]*)=([\s\S]*)$/;

/**
 * One word starting with `-`: read as the next flag, not as a value. `delete-history`'s parser reads
 * any such word after `--conversation-id` as no value (`-y`, and `-1` alike), and deletes everything.
 */
const FLAG_SHAPED = /^-\S*$/;

/**
 * Whether `arg` is a `--dry-run` flag, bare or valued, spelled as one shell word. A value holding
 * whitespace is prose a free-text flag took (`-e "--dry-run=true was applied"`), not the flag.
 */
export function isDryRunToken(arg: string): boolean {
  return /^--dry-run(=\S*)?$/.test(arg);
}

/** The refusal for an argument `invocation`'s parser does not read; `why` says what it reads instead. */
function unreadRefusal(invocation: string, token: string, why: string): string {
  const shown = token === '' ? '""' : sanitizeTerminalText(token);
  return `\`${invocation}\` does not read \`${shown}\`${why}, so it is refused rather than run without it.`;
}

/** The refusal for a `--dry-run` token `invocation` does not read; `readsBare` when it reads the bare flag. */
export function unreadDryRunRefusal(invocation: string, token: string, readsBare: boolean): string {
  return unreadRefusal(invocation, token, readsBare ? ' (only a bare `--dry-run`)' : '');
}

function noValueRefusal(invocation: string, flag: string): string {
  return `\`${invocation} ${flag}\` needs ${FLAG_TARGETS[flag] ?? 'a value'}, and none followed it.`;
}

/** `n` positional arguments, as a refusal says what a command takes. */
function positionalCount(n: number): string {
  return n === 0 ? 'no positional argument' : n === 1 ? 'one positional argument' : `${n} positional arguments`;
}

/**
 * The first of `args` (what follows the command's own words) that `reads` says its parser does not
 * read, as a refusal, or null. A spaced value is missing when nothing follows its flag, `""` does
 * (unless the parser reads it as a value), or a word starting with `-` does (`FLAG_SHAPED`):
 * unquoted, an unset `$ID` is no argument at all, so `--conversation-id $ID -y` arrives as
 * `--conversation-id -y`. A `--flag=<value>` value is missing only when empty.
 *
 * `splitsAtFirstLongFlag` (`SPLITS_AT_FIRST_LONG_FLAG`): every argument before the first `--` flag is
 * a positional, a short flag included, and a word after it that no flag took is read by none.
 */
function unreadArgumentRefusal(
  invocation: string,
  args: string[],
  reads: ParserReads,
  splitsAtFirstLongFlag: boolean,
): string | null {
  const takesValue = (flag: string) => reads.values?.includes(flag) === true;
  const missing = (value: string | undefined) =>
    value === undefined || FLAG_SHAPED.test(value) || (value === '' && reads.readsEmpty !== true);
  const takes = ` (it takes ${positionalCount(reads.positionals)})`;
  const firstLongFlag = args.findIndex((arg) => arg.startsWith('--'));
  const flagsFrom = !splitsAtFirstLongFlag ? 0 : firstLongFlag === -1 ? args.length : firstLongFlag;
  let positionals = 0;
  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (i < flagsFrom) {
      if (++positionals > reads.positionals) return unreadRefusal(invocation, arg, takes);
      continue;
    }
    const spelled = EQUALS_SPELLING.exec(arg);
    if (spelled) {
      const [, flag, value] = spelled;
      if (reads.equals?.includes(flag)) {
        if (value === '') return noValueRefusal(invocation, flag);
      } else if (takesValue(flag)) {
        return value === ''
          ? noValueRefusal(invocation, flag)
          : unreadRefusal(invocation, arg, ` (only \`${flag} <value>\`)`);
      } else {
        return unreadRefusal(invocation, arg, '');
      }
    } else if (takesValue(arg)) {
      if (missing(args[i + 1])) return noValueRefusal(invocation, arg);
      i++;
    } else if (!arg.startsWith('-')) {
      if (splitsAtFirstLongFlag) {
        return unreadRefusal(invocation, arg, ' (it reads positionals only before its first `--` flag)');
      }
      if (++positionals > reads.positionals) return unreadRefusal(invocation, arg, takes);
    }
  }
  return null;
}

/**
 * The refusal for an argument a hand-parsed command would skip and then run without, or null.
 *
 * The hand parsers read the flags they know by exact token and skip every other argument. So a
 * command ran without: a `--dry-run` it does not read (`wayai publish --dry-run -y` published); a
 * value flag spelled `--flag=<value>` (`wayai publish --hub=support -y` published the preview the
 * current directory is in, or the only one); a value flag given no value, which is what an unset
 * variable produces (`wayai delete-history --conversation-id $ID -y` deleted the whole history);
 * and a positional it does not read (`wayai publish -y support`). Each is refused, decided by what
 * the command's parser reads (`PARSER_READS`), never by what the command writes.
 *
 * Runs before dispatch, as `shouldInterceptHelp` does. The Data namespaces are commander programs,
 * which refuse an option they do not define, so they are left to commander. An invocation no entry
 * names (an unknown subcommand) is left to its dispatcher.
 */
export function entryFlagRefusal(
  command: string | undefined,
  args: string[],
  dataNamespaces: ReadonlySet<string>,
): string | null {
  if (command === undefined || HELP_AND_VERSION.includes(command)) return null;
  if (dataNamespaces.has(command)) return null;
  // The command and up to two subcommand words after it: `wayai admin notice create`, `wayai org push`.
  const words = [command];
  for (const arg of args.slice(0, 2)) {
    if (!/^[a-z][a-z-]*$/.test(arg)) break;
    words.push(arg);
  }
  // The longest leading words the table names: `admin hubs repair-team-orphans` before `admin`.
  let named = words.length;
  let reads = PARSER_READS.get(words.join(' '));
  while (reads === undefined && --named > 0) reads = PARSER_READS.get(words.slice(0, named).join(' '));
  const invocation = `wayai ${words.slice(0, named || words.length).join(' ')}`;
  if (reads === 'all') return null;

  const dryRuns = args.filter(isDryRunToken);
  const readsBare = typeof reads === 'object' && reads.dryRun === true;
  const refused = readsBare ? dryRuns.find((token) => token !== '--dry-run') : dryRuns[0];
  if (refused !== undefined) return unreadDryRunRefusal(invocation, refused, readsBare);

  if (reads === undefined || reads === 'selectors') return null;
  return unreadArgumentRefusal(invocation, args.slice(named - 1), reads, SPLITS_AT_FIRST_LONG_FLAG.has(command));
}

/** Returns true if `latest` is a newer semver than `current` */
export function isNewerVersion(latest: string, current: string): boolean {
  const strip = (v: string) => v.replace(/[-+].*$/, '');
  const l = strip(latest).split('.').map(Number);
  const c = strip(current).split('.').map(Number);
  for (let i = 0; i < Math.max(l.length, c.length); i++) {
    const lv = l[i] ?? 0;
    const cv = c[i] ?? 0;
    if (lv > cv) return true;
    if (lv < cv) return false;
  }
  return false;
}

/**
 * Decide where a secret comes from, and refuse the two combinations that fail
 * SILENTLY.
 *
 * Lives beside `promptSecret`/`readStdin` because it is the decision every
 * caller of them has to make, and getting it wrong is invisible in both
 * directions:
 *
 *  - **stdin on a TTY** — `readStdin` resolves only on `end`, which a terminal
 *    emits at Ctrl-D, so the command hangs having printed nothing WHILE the
 *    terminal driver echoes the typed secret in cleartext to screen and
 *    scrollback.
 *  - **prompt on a non-TTY** — readline hits EOF, `rl.question`'s callback never
 *    fires, the promise never settles, and node drains its loop and exits 0. A
 *    CI job goes green having created nothing.
 *
 * Neither shows in an exit code, which is why they were found one at a time
 * across five review rounds on one PR. `interactiveHint` is supplied by the
 * CALLER: only it knows which flags it declares, and a shared message naming
 * one caller's flag sends the other's users to an option that does not exist.
 */
export function resolveSecretSource(source: {
  /**
   * REQUIRED keys, not optional ones. Three separate defects in one PR were a
   * field added here and left unwired at a call site — an optional key is
   * invisible when forgotten, and no test can see a caller nobody wrote.
   * `undefined` is still a legal VALUE (a caller with no prompt flag passes it);
   * what the compiler now refuses is leaving the key out.
   */
  stdin: boolean | undefined;
  prompt: boolean | undefined;
  /** The caller's own flag spellings — a shared message naming one caller's flag misleads the others. */
  stdinFlag: string;
  promptHint: string;
}): 'stdin' | 'prompt' {
  // Contradictory input is REFUSED, not resolved. A precedence rule ("prompt
  // wins") had to answer a question with no right answer, and every answer left
  // an edge: with a pipe available it discarded the readable secret, and without
  // one it named the flag the caller had already passed.
  if (source.stdin && source.prompt) {
    throw expected(`${source.stdinFlag} and ${source.promptHint} are mutually exclusive — pass one.`);
  }
  if (source.stdin) {
    if (process.stdin.isTTY) {
      throw expected(
        `${source.stdinFlag} needs piped input. Pipe it — ` +
          `printf %s "$SECRET" | wayai … ${source.stdinFlag} — or ${source.promptHint}.`,
      );
    }
    return 'stdin';
  }
  if (!process.stdin.isTTY) {
    throw expected(
      `A secret cannot be prompted for when stdin is not a terminal. Pipe it instead: ` +
        `printf %s "$SECRET" | wayai … ${source.stdinFlag}`,
    );
  }
  return 'prompt';
}
